/**
 * RWBR — THE BOOTH: reading a feud.
 *
 * D-EZ-74. The rivalry angle existed before this file and it had exactly one
 * thing to say, three ways:
 *
 *     "Dale Ballew and Del Grubbs again"
 *     "these two are not done with each other"
 *     "Dale Ballew has been looking for Del Grubbs all afternoon"
 *
 * Which is not a rivalry. It is the WORD rivalry. The reason was structural:
 * `booth.ts` asked one question of the state —
 *
 *     hasRivalry: t.state.feuds.some((f) => …)
 *
 * — a boolean, because `FeudEntry` is `{ a: string; b: string }` and carries
 * nothing else. No turn, no cause, no count. Two men who have been wrecking
 * each other since April and two men who touched wheels ninety seconds ago are
 * the same value.
 *
 * ⚠ NOTHING NEW IS STORED. The engine was not changed to remember more. This
 * reads what the ledger already wrote:
 *
 *   - WHEN IT STARTED TODAY — the first turn whose `state.feuds` contains the
 *     pair. Feuds are only ever pushed, never removed, inside a race
 *     (`turn.ts`), so the first appearance IS the start.
 *   - WHAT STARTED IT — the event on that turn naming both men. The engine
 *     already wrote a sentence for it and cited the rule; we quote both.
 *   - WHETHER IT CAME IN THE HAULER — present on turn one means it started
 *     before today, and then the SEASON has to answer, not the race.
 *   - THE SEASON'S VERSION — `feudHistory.ts`, handed in through `BoothContext`
 *     when there is a season to hand it in from.
 *
 * That is the whole trick, and it is the same one the paper quotes used: the
 * feature was not impossible, something upstream was simply never being read.
 */

import type { RaceResult } from '../race.ts';
import { historyFor, type BoothContext } from './context.ts';

export interface FeudReading {
  /** The turn of THIS race on which the pair first appeared on the list. */
  startedTurn: number | null;
  /** The engine's own sentence for the moment it started, if it is in this race. */
  causeText: string | null;
  /** The rule the engine cited when it started. */
  causeCitation: string | null;
  /** True when the pair was already feuding when the green flag fell. */
  carriedIn: boolean;
  /** How many times these two have been in the same event so far today. */
  metToday: number;
  /** Which race of the season it started in — season context only. */
  sinceRace: number | null;
  /** The name of the track it started at — season context only. */
  sinceRaceName: string | null;
  /** How many races of this season it has been live for — season context only. */
  racesThisSeason: number | null;
  /** The season's own sentence for what started it — season context only. */
  seasonOriginText: string | null;
  /**
   * True when the race it started at is called the same thing as the one we are
   * at now. See `feudClause` — the season fact survives, the label does not.
   */
  sinceIsHere: boolean;
}

const pairIsFeuding = (feuds: readonly { a: string; b: string }[], a: string, b: string): boolean =>
  feuds.some((f) => (f.a === a && f.b === b) || (f.a === b && f.b === a));

/**
 * Everything true about this pair's grudge, at this point in this race.
 *
 * `upToTurn` matters and is not a convenience: the booth may never know
 * something the turn it is calling had not happened yet. A beat on turn 8 that
 * says "they have found each other three times" because the third one is on
 * turn 30 is exactly the D-003 violation this codebase keeps hunting.
 */
export function readFeud(
  race: RaceResult,
  a: string,
  b: string,
  upToTurn: number,
  ctx: BoothContext = {},
): FeudReading {
  let startedTurn: number | null = null;
  for (const t of race.turns) {
    if (t.turn > upToTurn) break;
    if (pairIsFeuding(t.state.feuds, a, b)) {
      startedTurn = t.turn;
      break;
    }
  }

  /*
    CARRIED IN. The grid is seeded from `RaceSetup.feuds` before a wheel turns,
    so a pair that is already on the list at the first turn we can see did not
    start it here. Turn 1's state is the state AFTER turn 1 resolved, so this is
    "by the end of the first turn", which is as fine a resolution as the ledger
    offers and is never wrong in the direction that matters — it can call a
    turn-one feud carried-in, and a turn-one feud is not a story anyway.
  */
  const carriedIn = startedTurn === 1 && (race.turns[0]?.state.feuds.length ?? 0) > 0
    ? pairIsFeuding(race.turns[0]?.state.feuds ?? [], a, b)
    : false;

  let causeText: string | null = null;
  let causeCitation: string | null = null;
  if (startedTurn !== null && !carriedIn) {
    const t = race.turns[startedTurn - 1];
    // The longest event naming both of them — the same "longest description
    // wins" rule `engineLine` uses, because the engine writes its fullest
    // sentence for the thing that actually happened.
    const found = [...(t?.events ?? [])]
      .filter((e) => e.actorIds.includes(a) && e.actorIds.includes(b))
      .sort((x, y) => y.description.length - x.description.length)[0];
    if (found) {
      causeText = found.description;
      causeCitation = found.ruleCitation;
    }
  }

  const metToday = race.events.filter(
    (e) => e.turn <= upToTurn && e.actorIds.includes(a) && e.actorIds.includes(b),
  ).length;

  const season = historyFor(ctx, a, b);

  /*
    ⚠ "IT STARTED AT DAYTONA" IS NOT A THING TO SAY AT DAYTONA — D-EZ-74.

    The season history includes the CURRENT race week, because that is what the
    dossier chevron needs: a feud struck in Friday's qualifying has to show a
    cause on the card before the green flag. For the booth that produces a
    grudge whose entire recorded history is the race we are watching, and the
    season phrasing then says "it started at Daytona 500" over pictures of
    Daytona 500.

    So a history that begins here is reported as no season history at all, and
    the reading falls through to the in-race phrasings — which are the truthful
    ones, because in-race is genuinely all there is. `ctx.raceNumber` is what
    makes the comparison possible and is the only reason it is on the context.
  */
  const startedHere = season !== null && ctx.raceNumber !== undefined && season.firstRace === ctx.raceNumber;
  const priorSeason = startedHere ? null : season;

  /*
    ⚠ AND A SCHEDULE VISITS THE SAME TRACK TWICE.

    Filtering on the race NUMBER was not enough, and the season test caught it
    inside a minute: a feud that genuinely started at an earlier Daytona still
    produced "it started at Daytona 500" while the field was going round
    Daytona. Every word true, and a listener cannot tell it apart from "it
    started here, just now" — which is the only thing that sentence can mean to
    somebody watching.

    The fix is not to drop the fact. It is to drop the LABEL and keep the count,
    which is the part that was never ambiguous. See `feudClause`.
  */
  const sinceIsHere =
    priorSeason?.origin?.raceName != null && priorSeason.origin.raceName === race.state.raceName;

  return {
    startedTurn,
    causeText,
    causeCitation,
    carriedIn,
    metToday,
    sinceRace: priorSeason?.firstRace ?? null,
    sinceRaceName: priorSeason?.origin?.raceName ?? null,
    racesThisSeason: priorSeason ? priorSeason.races.length : null,
    seasonOriginText: priorSeason?.origin?.text ?? null,
    sinceIsHere,
  };
}

/**
 * The reading as one clause a line can use, or null when there is nothing to
 * say that the generic phrasing does not already say better.
 *
 * Ordered by how much a listener would care, which is not the same as how much
 * data each carries: "since Bristol in April" beats "they have met four times"
 * because a date makes it a STORY and a count makes it a statistic.
 */
export function feudClause(read: FeudReading): string | null {
  const carried = read.racesThisSeason !== null && read.racesThisSeason >= 2;

  // The best version: a count AND a place. Only when the place is unambiguous.
  if (carried && read.sinceRaceName && !read.sinceIsHere) {
    return read.racesThisSeason === 2
      ? `this goes back to ${read.sinceRaceName}`
      : `this has been running since ${read.sinceRaceName} — ${read.racesThisSeason} races now`;
  }

  // The place is the racetrack we are at, so it cannot be used as a landmark.
  // The count is still true and still the interesting half.
  if (carried) {
    return read.racesThisSeason === 2
      ? `these two have been at it since the last time out`
      : `these two have been at it for ${read.racesThisSeason} races now`;
  }

  if (read.sinceRaceName && !read.sinceIsHere) return `it started at ${read.sinceRaceName}`;
  if (read.carriedIn) return `they came here already not speaking`;
  if (read.startedTurn !== null && read.metToday >= 3) {
    return `that is ${read.metToday} times these two have found each other today`;
  }
  if (read.startedTurn !== null && read.metToday === 2) return `the second time today`;
  if (read.startedTurn !== null) return `it started on turn ${read.startedTurn}`;
  return null;
}
